import classNames from 'classnames'
import { debounce } from 'lodash'
import React, { useState } from 'react'
import { CustomButton } from '../../../components'
import { useLiveViewStateStore, useZoomFocusStore } from '../LiveViewStore'
import { autofocusRequest } from '../api/requests'
import { useSupportedFunctionalities } from '../hooks'
import { FULLSCREEN_CONTROL_WIDTH } from '../LiveViewUI'
import { Control } from './Control'
import styles from './ZoomFocusControls.module.css'

export const ZoomFocusControls: React.FC<{ fullscreen: boolean }> = ({ fullscreen }) => {
    const {
        liveView,

        baseURL,
        setLoadingZoomFocus,
        loadingZoomFocus,
        transferZoomAndFocusToLiveView,
        areaFocusEnabled,
        setAreaFocusEnabled,
        setCheckDirtyLens
    } = useLiveViewStateStore()
    const { zoom, focus, setFocus } = useZoomFocusStore()
    const { IS_ZOOM_FOCUS_SUPPORTED, IS_REGIONAL_AUTOFOCUS_SUPPORTED } = useSupportedFunctionalities()
    const [tempZoom, setTempZoom] = useState<number | null>(null)
    const [tempFocus, setTempFocus] = useState<number | null>(null)

    // Debounce the zoom API request by 300ms
    const debouncedZoomUpdate = React.useCallback(
        debounce((value: number) => {
            transferZoomAndFocusToLiveView({ zoom: value, autofocus: true })
        }, 300),
        [baseURL]
    )

    // Debounce the focus API request by 300ms
    const debouncedFocusUpdate = React.useCallback(
        debounce((value: number) => {
            transferZoomAndFocusToLiveView({ focus: value, autofocus: false })
        }, 300),
        [baseURL]
    )

    React.useEffect(() => {
        const zoomChanged = tempZoom !== null && tempZoom === zoom
        if (zoomChanged) {
            // console.log('setting temp zoom to null', tempZoom, liveView.zoom)
            setTempZoom(null)
        }
        const focusChanged = tempFocus !== null && tempFocus === focus
        if (focusChanged) {
            // console.log('setting temp focus to null', tempFocus, liveView.focus)
            setTempFocus(null)
        }
    }, [zoom, focus])

    if (!IS_ZOOM_FOCUS_SUPPORTED) {
        return <>Zoom and focus controls are not supported on this device</>
    }

    return (
        <div>
            <div
                className={classNames(styles.controlsRow, { [styles.fullscreen]: fullscreen })}
                style={fullscreen ? { width: FULLSCREEN_CONTROL_WIDTH } : undefined}
            >
                <Control
                    width={fullscreen ? '100%' : '45%'}
                    label="Zoom"
                    min={liveView.ranges.zoom.min}
                    max={liveView.ranges.zoom.max}
                    step={liveView.ranges.focus.step}
                    value={tempZoom ?? zoom}
                    disabled={loadingZoomFocus}
                    setValue={(value) => {
                        setTempZoom(value)
                        debouncedZoomUpdate(value)
                        setCheckDirtyLens(false)
                    }}
                    fullscreen={fullscreen}
                />
                {fullscreen && <br />}
                <Control
                    width={fullscreen ? '100%' : '45%'}
                    label="Focus"
                    min={liveView.ranges.focus.min}
                    max={liveView.ranges.focus.max}
                    step={liveView.ranges.focus.step}
                    value={tempFocus ?? focus}
                    disabled={loadingZoomFocus}
                    setValue={(value) => {
                        setTempFocus(value)
                        debouncedFocusUpdate(value)
                        setCheckDirtyLens(false)
                    }}
                    fullscreen={fullscreen}
                />
            </div>

            <div className={styles.actionsRow}>
                <div className={classNames(styles.actionWrapper, { [styles.fullscreen]: fullscreen })}>
                    <CustomButton
                        extraProps={{ minWidth: '150px' }}
                        disabled={loadingZoomFocus}
                        onClick={() => {
                            setLoadingZoomFocus(true)

                            autofocusRequest(baseURL)
                                .then((response) => {
                                    if (response.data.focus) {
                                        setFocus(response.data.focus)
                                    }
                                })
                                .catch(() => {
                                    // Failure already surfaced (message + log) in createGenericRequest;
                                    // catch here so the rejection isn't an unhandled promise rejection.
                                })
                                .finally(() => {
                                    setLoadingZoomFocus(false)
                                })
                        }}
                    >
                        Trigger Autofocus
                    </CustomButton>
                </div>
                <div className={classNames(styles.actionWrapper, { [styles.fullscreen]: fullscreen })}>
                    <CustomButton
                        isActive={areaFocusEnabled}
                        extraProps={{ minWidth: '150px' }}
                        disabled={loadingZoomFocus || !IS_REGIONAL_AUTOFOCUS_SUPPORTED}
                        onClick={() => {
                            setAreaFocusEnabled(!areaFocusEnabled)
                        }}
                    >
                        {IS_REGIONAL_AUTOFOCUS_SUPPORTED
                            ? areaFocusEnabled
                                ? 'Disable Area Focus'
                                : 'Enable Area Focus'
                            : 'Regional Autofocus not supported'}
                    </CustomButton>
                </div>
            </div>
        </div>
    )
}
